import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import PortfolioOverview from './components/PortfolioOverview';
import AssetAllocationChart from './components/AssetAllocationChart';
import HoldingsTable from './components/HoldingsTable';
import PerformanceChart from './components/PerformanceChart';
import TransactionHistory from './components/TransactionHistory';
import AddPositionModal from './components/AddPositionModal';
import FilterPanel from './components/FilterPanel';

const PortfolioManagement = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'symbol',
    sortOrder: 'asc',
    sector: 'all',
    performance: 'all',
    positionSize: 'all',
    priceRange: { min: '', max: '' }
  });

  // Mock portfolio data
  const portfolioData = {
    totalValue: 10434430.50,
    todayChange: 194340.25,
    todayChangePercent: 1.9,
    unrealizedGain: 1562340.30,
    unrealizedGainPercent: 17.6
  };

  // Mock asset allocation data
  const allocationData = [
    { name: 'Technology', value: 3762340.50, percentage: 36.1 },
    { name: 'Healthcare', value: 2392340.25, percentage: 22.9 },
    { name: 'Financials', value: 1845340.75, percentage: 17.7 },
    { name: 'Consumer Discretionary', value: 1302340.30, percentage: 12.5 },
    { name: 'Communication Services', value: 742340.45, percentage: 7.1 },
    { name: 'Industrials', value: 391840.25, percentage: 3.8 }
  ];

  // Mock holdings data
  const [holdings, setHoldings] = useState([
    {
      symbol: 'AAPL',
      companyName: 'Apple Inc.',
      shares: 150,
      avgCost: 13750.50,
      currentPrice: 14600.43,
      marketValue: 2190064.50,
      sector: 'technology'
    },
    {
      symbol: 'GOOGL',
      companyName: 'Alphabet Inc.',
      shares: 8,
      avgCost: 220500.00,
      currentPrice: 236934.63,
      marketValue: 1895477.04,
      sector: 'technology'
    },
    {
      symbol: 'MSFT',
      companyName: 'Microsoft Corporation',
      shares: 45,
      avgCost: 28320.25,
      currentPrice: 31520.85,
      marketValue: 1418438.25,
      sector: 'technology'
    },
    {
      symbol: 'JNJ',
      companyName: 'Johnson & Johnson',
      shares: 120,
      avgCost: 13210.75,
      currentPrice: 13510.30,
      marketValue: 1621236.00,
      sector: 'healthcare'
    },
    {
      symbol: 'JPM',
      companyName: 'JPMorgan Chase & Co.',
      shares: 85,
      avgCost: 12130.80,
      currentPrice: 12680.45,
      marketValue: 1077838.25,
      sector: 'financials'
    },
    {
      symbol: 'TSLA',
      companyName: 'Tesla Inc.',
      shares: 25,
      avgCost: 18330.30,
      currentPrice: 20680.50,
      marketValue: 517012.50,
      sector: 'consumer-discretionary'
    },
    {
      symbol: 'NVDA',
      companyName: 'NVIDIA Corporation',
      shares: 12,
      avgCost: 65340.40,
      currentPrice: 72840.28,
      marketValue: 874083.36,
      sector: 'technology'
    },
    {
      symbol: 'UNH',
      companyName: 'UnitedHealth Group Inc.',
      shares: 18,
      avgCost: 40370.20,
      currentPrice: 42640.75,
      marketValue: 767533.50,
      sector: 'healthcare'
    }
  ]);

  // Mock performance data
  const performanceData = {
    '1D': [
      { date: '9:30 AM', portfolio: 1024340.25, sp500: 376234.15 },
      { date: '10:00 AM', portfolio: 1025340.80, sp500: 376534.30 },
      { date: '11:00 AM', portfolio: 1034340.45, sp500: 377234.75 },
      { date: '12:00 PM', portfolio: 1039340.20, sp500: 377834.20 },
      { date: '1:00 PM', portfolio: 1044340.50, sp500: 378434.85 },
      { date: '2:00 PM', portfolio: 1042340.75, sp500: 378134.60 },
      { date: '3:00 PM', portfolio: 1044340.50, sp500: 378434.85 }
    ],
    '1W': [
      { date: 'Mon', portfolio: 1019340.30, sp500: 373234.20 },
      { date: 'Tue', portfolio: 1025340.75, sp500: 374234.85 },
      { date: 'Wed', portfolio: 1035340.40, sp500: 375534.30 },
      { date: 'Thu', portfolio: 1031340.25, sp500: 374834.75 },
      { date: 'Fri', portfolio: 1044340.50, sp500: 378434.85 }
    ],
    '1M': [
      { date: 'Dec 11', portfolio: 988340.25, sp500: 367834.15 },
      { date: 'Dec 18', portfolio: 1001340.80, sp500: 369834.30 },
      { date: 'Dec 25', portfolio: 997340.45, sp500: 369134.75 },
      { date: 'Jan 1', portfolio: 1012340.20, sp500: 371534.20 },
      { date: 'Jan 8', portfolio: 1025340.75, sp500: 373234.85 },
      { date: 'Jan 15', portfolio: 1039340.30, sp500: 375534.30 },
      { date: 'Jan 22', portfolio: 1029340.85, sp500: 374234.75 },
      { date: 'Jan 29', portfolio: 1044340.50, sp500: 378434.85 }
    ]
  };

  // Mock transaction history
  const transactions = [
    {
      id: 1,
      date: '2025-01-10',
      type: 'buy',
      symbol: 'AAPL',
      companyName: 'Apple Inc.',
      quantity: 25,
      price: 14600.43,
      total: 365010.75
    },
    {
      id: 2,
      date: '2025-01-08',
      type: 'sell',
      symbol: 'MSFT',
      companyName: 'Microsoft Corporation',
      quantity: 10,
      price: 31520.85,
      total: 315208.50
    },
    {
      id: 3,
      date: '2025-01-05',
      type: 'buy',
      symbol: 'NVDA',
      companyName: 'NVIDIA Corporation',
      quantity: 5,
      price: 72840.28,
      total: 364201.40
    },
    {
      id: 4,
      date: '2025-01-03',
      type: 'dividend',
      symbol: 'JNJ',
      companyName: 'Johnson & Johnson',
      quantity: 120,
      price: 99.19,
      total: 11902.80
    },
    {
      id: 5,
      date: '2024-12-28',
      type: 'buy',
      symbol: 'GOOGL',
      companyName: 'Alphabet Inc.',
      quantity: 3,
      price: 236934.63,
      total: 710803.89
    }
  ];

  const handleBuyMore = (symbol) => {
    console.log(`Buy more ${symbol}`);
    // Implement buy more functionality
  };

  const handleSell = (symbol) => {
    console.log(`Sell ${symbol}`);
    // Implement sell functionality
  };

  const handleViewDetails = (symbol) => {
    console.log(`View details for ${symbol}`);
    // Implement view details functionality
  };

  const handleAddPosition = (orderData) => {
    console.log('Adding new position:', orderData);
    // Implement add position functionality
    // This would typically make an API call to execute the order
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    console.log('Applying filters:', newFilters);
    // Implement filter logic here
  };

  // Real-time price updates simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setHoldings(prevHoldings => 
        prevHoldings.map(holding => ({
          ...holding,
          currentPrice: holding.currentPrice + (Math.random() - 0.5) * 2,
          marketValue: (holding.currentPrice + (Math.random() - 0.5) * 2) * holding.shares
        }))
      );
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>Portfolio Management - StockTracker Pro</title>
        <meta name="description" content="Manage your investment portfolio with comprehensive tracking, analysis, and real-time market data." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex">
          <Sidebar />
          
          <main className="flex-1 p-4 lg:p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Portfolio Management</h1>
                <p className="text-muted-foreground mt-1">
                  Track and analyze your investment holdings with real-time market data
                </p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(true)}
                  className="md:hidden"
                >
                  <Icon name="Filter" size={16} />
                  <span className="ml-2">Filter</span>
                </Button>
                
                <Button onClick={() => setShowAddModal(true)}>
                  <Icon name="Plus" size={16} />
                  <span className="ml-2">Add Position</span>
                </Button>
              </div>
            </div>

            {/* Portfolio Overview Cards */}
            <PortfolioOverview portfolioData={portfolioData} />

            {/* Asset Allocation Chart */}
            <AssetAllocationChart allocationData={allocationData} />

            {/* Performance Chart */}
            <PerformanceChart performanceData={performanceData} />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Holdings Table */}
              <div className="lg:col-span-3">
                <HoldingsTable
                  holdings={holdings}
                  onBuyMore={handleBuyMore}
                  onSell={handleSell}
                  onViewDetails={handleViewDetails}
                />
              </div>

              {/* Desktop Filter Panel */}
              <div className="hidden lg:block">
                <FilterPanel
                  isOpen={true}
                  onClose={() => {}}
                  onApplyFilters={handleApplyFilters}
                  currentFilters={filters}
                />
              </div>
            </div>

            {/* Transaction History */}
            <TransactionHistory transactions={transactions} />

            {/* Export Section */}
            <div className="card-elevated p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Export Portfolio Data</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Generate reports for tax purposes or record keeping
                  </p>
                </div>
                
                <div className="flex space-x-3 mt-4 sm:mt-0">
                  <Button variant="outline" size="sm">
                    <Icon name="FileText" size={16} />
                    <span className="ml-2">Export PDF</span>
                  </Button>
                  <Button variant="outline" size="sm">
                    <Icon name="Download" size={16} />
                    <span className="ml-2">Export CSV</span>
                  </Button>
                </div>
              </div>
            </div>
          </main>
        </div>

        {/* Floating Action Button (Mobile) */}
        <Button
          onClick={() => setShowAddModal(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg md:hidden z-navigation"
          size="icon"
        >
          <Icon name="Plus" size={24} />
        </Button>

        {/* Modals */}
        <AddPositionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddPosition={handleAddPosition}
        />

        {/* Mobile Filter Panel */}
        <FilterPanel
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApplyFilters={handleApplyFilters}
          currentFilters={filters}
        />
      </div>
    </>
  );
};

export default PortfolioManagement;